import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { displayStoredUploadFilename } from "../utils/displayUploadFilename";
import { saveFileAs } from "../utils/fileDownload";
import { blurHashToDataUrl } from "../utils/blurHashPaint";
import { isPreviewableImagePath } from "../utils/chatDropFiles";
import { toSameOriginMediaUrl } from "../utils/mediaPlayUrl";

type Props = {
  href: string;
  fileNameRaw?: string | null;
  fileSizeRaw?: string | null;
  /** Tiny LQIP / thumb for image-as-doc */
  previewUrl?: string | null;
  blurHash?: string | null;
  isOwn?: boolean;
  theme?: string;
};

const sizeCache = new Map<string, number>();

/** 126.2KB · 1.25MB · 512B */
export function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${Math.round(bytes)}B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb >= 100 ? kb.toFixed(0) : kb.toFixed(1)}KB`;
  }
  const mb = bytes / (1024 * 1024);
  const s = mb >= 100 ? mb.toFixed(0) : mb.toFixed(2);
  return `${s.replace(/\.?0+$/, "")}MB`;
}

function parseSizeToBytes(raw?: string | null): number | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  const m = trimmed.match(/^([\d.]+)\s*(B|KB|MB|GB)$/i);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 0) return null;
  const u = m[2].toUpperCase();
  if (u === "B") return n;
  if (u === "KB") return n * 1024;
  if (u === "MB") return n * 1024 * 1024;
  if (u === "GB") return n * 1024 * 1024 * 1024;
  return null;
}

function fileExtLabel(name: string, href: string): string {
  const fromName = name.match(/\.([a-zA-Z0-9]{1,8})$/);
  if (fromName) return fromName[1].toUpperCase();
  const fromUrl = href.replace(/\\/g, "/").split("/").pop() || "";
  const m = fromUrl.match(/\.([a-zA-Z0-9]{1,8})(?:\?|$)/);
  return m ? m[1].toUpperCase() : "FILE";
}

async function fetchContentLength(url: string): Promise<number | null> {
  const cached = sizeCache.get(url);
  if (cached != null) return cached;
  try {
    const res = await fetch(url, { method: "HEAD", credentials: "omit" });
    const len = res.headers.get("content-length");
    if (!len) return null;
    const n = Number(len);
    if (!Number.isFinite(n) || n <= 0) return null;
    sizeCache.set(url, n);
    return n;
  } catch {
    return null;
  }
}

/**
 * Round icon · filename / PDF · size
 * Image-as-doc: square preview + blur_hash + download arrow (no colored chip).
 * Click: Save As.
 */
export default function DocMessageBubble({
  href,
  fileNameRaw,
  fileSizeRaw,
  previewUrl,
  blurHash,
  isOwn = false,
  theme = "light",
}: Props) {
  const name = displayStoredUploadFilename(href, fileNameRaw || undefined);
  const ext = fileExtLabel(name, href);
  const initialBytes = parseSizeToBytes(fileSizeRaw);
  const [bytes, setBytes] = useState<number | null>(initialBytes);
  const [imgLoaded, setImgLoaded] = useState(false);

  const showImagePreview = isPreviewableImagePath(href, name);
  const thumbSrc = useMemo(() => {
    if (!showImagePreview) return "";
    const raw = String(previewUrl || "").trim() || href;
    return toSameOriginMediaUrl(raw) || raw;
  }, [showImagePreview, previewUrl, href]);

  const blurDataUrl = useMemo(() => {
    if (!showImagePreview) return null;
    const h = String(blurHash || "").trim();
    if (h.length < 6) return null;
    return blurHashToDataUrl(h, 32, 32, 0.85);
  }, [showImagePreview, blurHash]);

  useEffect(() => {
    setBytes(parseSizeToBytes(fileSizeRaw));
  }, [fileSizeRaw, href]);

  useEffect(() => {
    setImgLoaded(false);
  }, [thumbSrc]);

  useEffect(() => {
    if (bytes != null || !href) return;
    let cancelled = false;
    fetchContentLength(href).then((n) => {
      if (!cancelled && n != null) setBytes(n);
    });
    return () => {
      cancelled = true;
    };
  }, [href, bytes]);

  const sizeLabel = bytes != null ? formatBytesShort(bytes) : "";
  const metaLine = sizeLabel ? `${ext} · ${sizeLabel}` : ext;
  const dark = theme === "dark";

  const iconBg = isOwn
    ? dark
      ? "bg-white/95"
      : "bg-white"
    : "bg-[var(--theme-color,#50a2e3)]";
  const iconFg = isOwn
    ? "text-black/75"
    : "text-[var(--theme-text-color,#fff)]";
  const titleColor = isOwn
    ? "text-black"
    : dark
      ? "text-white"
      : "text-black";
  const metaColor = isOwn
    ? "text-black/50"
    : dark
      ? "text-white/50"
      : "text-black/45";

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void saveFileAs(href, name);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-[min(17.5rem,70vw)] cursor-pointer items-center gap-2.5 py-0.5 text-left"
    >
      {showImagePreview ? (
        <div
          className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-black/10"
          aria-hidden
        >
          {blurDataUrl ? (
            <img
              src={blurDataUrl}
              alt=""
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                imgLoaded ? "opacity-0" : "opacity-100"
              }`}
            />
          ) : null}
          {thumbSrc ? (
            <img
              src={thumbSrc}
              alt=""
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                imgLoaded ? "opacity-100" : "opacity-0"
              }`}
              onLoad={() => setImgLoaded(true)}
            />
          ) : null}
          {/* download arrow — always subtle, stronger on hover */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-70 transition-opacity duration-200 group-hover:bg-black/40 group-hover:opacity-100">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="text-white drop-shadow scale-90 transition duration-200 group-hover:scale-100"
            >
              <path
                d="M12 3v12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M7 11l5 5 5-5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 20h14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      ) : (
        <div
          className={`relative h-11 w-11 shrink-0 overflow-hidden rounded-full ${iconBg}`}
          aria-hidden
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition duration-200 ease-out group-hover:scale-75 group-hover:opacity-0 ${iconFg}`}
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
            <path
              d="M14 2v6h6"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
            <path
              d="M8 13h8M8 17h5"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>

          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-75 opacity-0 transition duration-200 ease-out group-hover:scale-100 group-hover:opacity-100 ${iconFg}`}
          >
            <path
              d="M12 3v12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M7 11l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 20h14"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p
          className={`line-clamp-2 text-sm font-medium leading-snug ${titleColor}`}
        >
          {name}
        </p>
        <p
          className={`mt-1.5 text-[11px] leading-none tabular-nums ${metaColor}`}
        >
          {metaLine}
        </p>
      </div>
    </button>
  );
}
